import { ArrowUpRight, Info } from "lucide-react";
import { trackEvent } from "@/lib/track";

function ytId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? m[1] : (/^[\w-]{11}$/.test(url) ? url : null);
}

function Block({ block, postId }) {
  const t = block.type;

  if (t === "heading") {
    const Tag = block.level === 3 ? "h3" : "h2";
    return <Tag className={`font-display font-bold uppercase text-white ${block.level === 3 ? "text-xl lg:text-2xl mt-10" : "text-2xl lg:text-4xl mt-14"} mb-4 leading-tight scroll-mt-24`}>{block.text}</Tag>;
  }
  if (t === "text") return <div className="article-body" dangerouslySetInnerHTML={{ __html: block.html || "" }} />;
  if (t === "list") {
    const items = block.items || [];
    const inline = (s) => (s || "").replace(/<\/?(p|div)[^>]*>/gi, "");
    return (
      <div className="article-body">
        {block.style === "ordered"
          ? <ol>{items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}</ol>
          : <ul>{items.map((it, i) => <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />)}</ul>}
      </div>
    );
  }
  if (t === "quote") {
    return (
      <figure className="my-12 border-l-4 border-brand pl-6 lg:-ml-6">
        <blockquote className="font-display uppercase text-white text-2xl lg:text-3xl leading-tight">{block.text}</blockquote>
        {block.cite && <figcaption className="mt-4 font-hand text-brand text-xl">{block.cite}</figcaption>}
      </figure>
    );
  }
  if (t === "pullquote") {
    return (
      <div className="my-14 lg:-mx-16">
        <p className="font-poster uppercase text-center leading-[0.95] text-3xl sm:text-4xl lg:text-6xl text-white">
          {block.text}
        </p>
        <div className="w-24 h-1 bg-brand mx-auto mt-6" />
      </div>
    );
  }
  if (t === "callout") {
    return (
      <div className="my-10 relative border border-ink-600 texture-concrete p-6 lg:p-7">
        <div className="absolute top-0 left-0 w-1.5 h-full bg-brand" />
        <div className="flex gap-4 items-start pl-2">
          <Info size={20} className="text-brand shrink-0 mt-0.5" />
          <div className="text-white/85 text-base lg:text-lg leading-relaxed">{block.text}</div>
        </div>
      </div>
    );
  }
  if (t === "divider") return <div className="my-12 flex items-center gap-3"><span className="w-10 h-1 bg-brand" /><span className="flex-1 h-px bg-ink-600" /></div>;
  if (t === "image") {
    const inner = (
      <figure className="my-10">
        <div className="border border-ink-600 overflow-hidden"><img src={block.url} alt={block.alt || ""} loading="lazy" className={`w-full ${block.align === "center" ? "mx-auto" : ""}`} style={block.width ? { maxWidth: block.width } : {}} /></div>
        {block.caption && <figcaption className="mt-2 font-cond uppercase tracking-wider text-xs text-white/45 text-center">{block.caption}</figcaption>}
      </figure>
    );
    return block.link ? <a href={block.link} target="_blank" rel="noopener noreferrer">{inner}</a> : inner;
  }
  if (t === "gallery") {
    const imgs = block.images || [];
    return <div className="my-10 grid grid-cols-2 md:grid-cols-3 gap-2">{imgs.map((im, i) => <img key={i} src={im.url || im} alt={im.alt || ""} loading="lazy" className="border border-ink-600 w-full h-44 object-cover" />)}</div>;
  }
  if (t === "youtube" || t === "video") {
    const id = ytId(block.url);
    if (id) return <div className="my-10 relative w-full border border-ink-600 overflow-hidden" style={{ paddingBottom: "56.25%" }}><iframe className="absolute inset-0 w-full h-full" src={`https://www.youtube.com/embed/${id}`} title="Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>;
    return <div className="my-10 relative w-full border border-ink-600 overflow-hidden" style={{ paddingBottom: "56.25%" }}><video controls className="absolute inset-0 w-full h-full" src={block.url} /></div>;
  }
  if (t === "button" || t === "affiliate") {
    return (
      <div className="my-10">
        <a href={block.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackEvent("outbound_click", { post_id: postId, target_id: block.id })} className="btn-poster">
          <span>{(block.text || "Learn more").toUpperCase()}</span><ArrowUpRight size={17} className="relative z-10" />
        </a>
        {t === "affiliate" && block.disclosure && <p className="mt-2 text-xs text-white/45">{block.disclosure}</p>}
      </div>
    );
  }
  if (t === "product") {
    return (
      <div className="my-10 border border-ink-600 texture-concrete p-5 flex gap-4 items-center">
        {block.image && <img src={block.image} alt={block.name} className="w-16 h-16 object-cover border border-ink-600" />}
        <div className="flex-1"><div className="font-display font-bold uppercase text-white">{block.name}</div>{block.description && <div className="text-sm text-white/55">{block.description}</div>}</div>
        {block.url && <a href={block.url} target="_blank" rel="noopener noreferrer sponsored" onClick={() => trackEvent("outbound_click", { post_id: postId, target_id: block.id })} className="font-cond uppercase tracking-wider text-sm font-bold text-brand hover:text-brand-bright flex items-center gap-1">View <ArrowUpRight size={14} /></a>}
      </div>
    );
  }
  if (t === "html" || t === "embed") return <div className="my-10" dangerouslySetInnerHTML={{ __html: block.html || "" }} />;
  return null;
}

export default function BlockRenderer({ blocks = [], postId }) {
  return <div>{blocks.map((b, i) => <Block key={b.id || i} block={b} postId={postId} />)}</div>;
}
